import { memoize } from "lodash";
import { TOption, TPermissions } from "../../Types";
import i18n from "../../constants/i18n";
import { IComputedValue, computed } from "mobx";
import getCorePersonalStore from "../core/core-personal-store";
import { getPermissionIDRequired } from "../../helpers/et3-has-permission";
import { isPositiveNumber } from "et3-type-guards";

class RoutingStore {
  USER_OPTIONS: IComputedValue<TOption[]> = computed(() => {
    const userPermissions = getCorePersonalStore().userPermissions.get();
    const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();
    const hasUserMessagesPermission = isPositiveNumber(
      userPermissions.indexOf(getPermissionIDRequired("User-Messages")),
      true
    );

    const userOptionsArray: TOption[] = [
      {
        title: i18n.get("ADVANCED"),
        link: "/users/advanced",
        name: TPermissions.User,
        pageName: "USER_ADVANCED",
        pageSubName: "USER_ADVANCED",
        visibility: true
      },
      {
        title: i18n.get("CONSUMABLES"),
        link: "/users/consumables",
        name: TPermissions.User,
        pageName: "USER_CONSUMABLES",
        pageSubName: "USER_CONSUMABLES",
        visibility: true
      },
      {
        title: i18n.get("CONVERSATIONS"),
        link: "/users/conversations",
        name: TPermissions.User,
        pageName: "USER_CONVERSATIONS",
        pageSubName: "USER_CONVERSATIONS",
        visibility: hasUserMessagesPermission
      },
      {
        title: i18n.get("SILENTLY_BANNED_USERS"),
        link: "/users/silentlyBanned",
        name: TPermissions.User,
        pageName: "SILENTLY_BANNED_USERS",
        pageSubName: "SILENTLY_BANNED_USERS",
        visibility: true
      },
      {
        title: i18n.get("DEVICES"),
        link: "/users/devices",
        name: TPermissions.User,
        pageName: "USER_DEVICES",
        pageSubName: "USER_DEVICES",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("USER_EDIT_PROFILE"),
        link: "/users/editProfile",
        name: TPermissions.User,
        pageName: "USER_EDIT_PROFILE",
        pageSubName: "USER_EDIT_PROFILE",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("EVENTS"),
        link: "/users/events",
        name: TPermissions.User,
        pageName: "USER_EVENTS",
        pageSubName: "USER_EVENTS",
        visibility: true
      },
      {
        title: i18n.get("LOGIN"),
        link: "/users/login",
        name: TPermissions.User,
        pageName: "USER_LOGIN",
        pageSubName: "USER_LOGIN",
        visibility: !isAnyKhattabaAdmin
      },

      {
        title: i18n.get("MESSAGES"),
        link: "/users/messages",
        name: TPermissions.User,
        pageName: "USER_MESSAGES",
        pageSubName: "USER_MESSAGES",
        visibility: hasUserMessagesPermission
      },
      {
        title: i18n.get("MEESAGE_ID"),
        link: "/users/messageid",
        name: TPermissions.User,
        pageName: "USER_MESSAGE_ID",
        pageSubName: "USER_MESSAGE_ID",
        visibility: hasUserMessagesPermission
      },

      {
        title: i18n.get("PAYMENTS"),
        link: "/users/payments",
        name: TPermissions.User,
        pageName: "USER_PAYMENTS",
        pageSubName: "USER_PAYMENTS",
        visibility: true
      },
      {
        title: i18n.get("REFERRAL_CODE_USAGE"),
        link: "/users/referralCodeUsage",
        name: TPermissions.User,
        pageName: "USER_REFERRAL_CODE_USAGE",
        pageSubName: "USER_REFERRAL_CODE_USAGE",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("PROMO_CODE_USAGE"),
        link: "/users/promoCodeUsage",
        name: TPermissions.User,
        pageName: "USER_PROMO_CODE_USAGE",
        pageSubName: "USER_PROMO_CODE_USAGE",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("SELFIE_VERIFICATION_PHOTOS"),
        link: "/users/selfieVerificationPhotos",
        name: TPermissions.User,
        pageName: "USER_SELFIE_VERIFICATION_PHOTOS",
        pageSubName: "USER_SELFIE_VERIFICATION_PHOTOS",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("AI_DESCRIPTIONS"),
        link: "/users/aiDescriptions",
        name: TPermissions.SCSR,
        pageName: "USER_AI_DESCRIPTIONS",
        pageSubName: "USER_AI_DESCRIPTIONS",
        visibility: true
      }
    ];
    return userOptionsArray;
  });

  CSR_OPTIONS: IComputedValue<TOption[]> = computed(() => {
    const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();

    const csrOptionsArray: TOption[] = [
      {
        title: i18n.get("CHANGE_PASSWORD"),
        link: "/csr/changePassword",
        name: TPermissions.CSR,
        pageName: "CHANGE_PASSWORD",
        pageSubName: "CHANGE_PASSWORD",
        visibility: true
      },
      {
        title: i18n.get("MY_CSR_PERFORMANCE"),
        link: "/csr/my-csr-performance",
        name: TPermissions.CSR,
        pageName: "CSR_MY_PERFORMANCE",
        pageSubName: "CSR_MY_PERFORMANCE",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("MY_CSR_MISTAKES"),
        link: "/csr/my-csr-mistakes",
        name: TPermissions.CSR,
        pageName: "CSR_MY_MISTAKES",
        pageSubName: "CSR_MY_MISTAKES",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("MY_CSR_PREV_ACTIONS"),
        link: "/csr/my-csr-prev-actions",
        name: TPermissions.CSR,
        pageName: "CSR_MY_PREV_ACTIONS",
        pageSubName: "CSR_MY_PREV_ACTIONS",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("CSR_MY_RECEIPT_EVENT_COUNT"),
        link: "/csr/myCSRReceiptEventCount",
        name: TPermissions.CSR,
        pageName: "CSR_MY_RECEIPT_EVENT_COUNT",
        pageSubName: "CSR_MY_RECEIPT_EVENT_COUNT",
        visibility: !isAnyKhattabaAdmin
      }
    ];
    return csrOptionsArray;
  });

  AUDITOR_OPTIONS: IComputedValue<TOption[]> = computed(() => {
    const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();

    const auditorOptionsArray: TOption[] = [
      {
        title: i18n.get("AUDIT"),
        link: "/auditor/audit",
        name: TPermissions.Auditor,
        pageName: "AUDITOR_AUDIT",
        pageSubName: "AUDITOR_AUDIT",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("AUDITED_EVENTS"),
        link: "/auditor/audited-events",
        name: TPermissions.Auditor,
        pageName: "AUDITOR_AUDITED_EVENTS",
        pageSubName: "AUDITOR_AUDITED_EVENTS",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("AUDITOR_EVENTS_DATA"),
        link: "/auditor/eventsData",
        name: TPermissions.Auditor,
        pageName: "AUDITOR_EVENTS_DATA",
        pageSubName: "AUDITOR_EVENTS_DATA",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("MY_AUDITING_PERFORMANCE"),
        link: "/auditor/my-auditing-performance",
        name: TPermissions.Auditor,
        pageName: "AUDITOR_MY_AUDITING_PERFORMANCE",
        pageSubName: "AUDITOR_MY_AUDITING_PERFORMANCE",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("MY_AUDITING_MISTAKES"),
        link: "/auditor/my-auditing-mistakes",
        name: TPermissions.Auditor,
        pageName: "AUDITOR_MY_AUDITING_MISTAKES",
        pageSubName: "AUDITOR_MY_AUDITING_MISTAKES",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("OTHER_CSRS_PERFORMANCE"),
        link: "/auditor/csrs-performance",
        name: TPermissions.Auditor,
        pageName: "AUDITOR_OTHER_CSR_PERFORMANCE",
        pageSubName: "AUDITOR_OTHER_CSR_PERFORMANCE",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("REPORT_ACTIONS_ANALYTICS"),
        link: "/auditor/report-actions-analytics",
        name: TPermissions.Auditor,
        pageName: "AUDITOR_REPORT_ACTIONS_ANALYTICS",
        pageSubName: "AUDITOR_REPORT_ACTIONS_ANALYTICS",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("EVENTS_CHART"),
        link: "/auditor/eventCharts",
        name: TPermissions.Auditor,
        pageName: "AUDITOR_EVENTS_CHARTS",
        pageSubName: "AUDITOR_EVENTS_CHARTS",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: "Pool",
        link: "/auditor/pool",
        name: TPermissions.Auditor,
        pageName: "AUDITOR_POOL",
        pageSubName: "AUDITOR_POOL",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("WORKLOAD_DATA"),
        link: "/auditor/workloadData",
        name: TPermissions.Auditor,
        pageName: "AUDITOR_WORKLOAD_DATA",
        pageSubName: "AUDITOR_WORKLOAD_DATA",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("PROMO_CODE"),
        link: "/auditor/promoCode",
        name: TPermissions.Auditor,
        pageName: "AUDITOR_PROMO_CODE",
        pageSubName: "AUDITOR_PROMO_CODE",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("AUDITOR_WEB_PAYMENTS"),
        link: "/auditor/webPayments",
        name: TPermissions.Auditor,
        pageName: "AUDITOR_WEB_PAYMENTS",
        pageSubName: "AUDITOR_WEB_PAYMENTS",
        visibility: true
      },
      {
        title: i18n.get("USER_RATE_US_DATA"),
        link: "/auditor/userRateUsData",
        name: TPermissions.Auditor,
        pageName: "AUDITOR_USER_RATE_US_DATA",
        pageSubName: "AUDITOR_USER_RATE_US_DATA",
        visibility: !isAnyKhattabaAdmin
      }
    ];
    return auditorOptionsArray;
  });

  SUPERVISOR_OPTIONS: IComputedValue<TOption[]> = computed(() => {
    const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();

    const supervisorOptionsArray: TOption[] = [
      {
        title: i18n.get("PERMISSION"),
        link: "/supervisor/permission",
        name: TPermissions.Supervisor,
        pageName: "SUPERVISOR_PERMISSION",
        pageSubName: "SUPERVISOR_PERMISSION",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("SUPERVISE"),
        link: "/supervisor/supervise",
        name: TPermissions.Supervisor,
        pageName: "SUPERVISOR_SUPERVISE",
        pageSubName: "SUPERVISOR_SUPERVISE",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("SUPERVISED_EVENTS"),
        link: "/supervisor/supervised-events",
        name: TPermissions.Supervisor,
        pageName: "SUPERVISOR_SUPERVISED_EVENTS",
        pageSubName: "SUPERVISOR_SUPERVISED_EVENTS",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("OTHER_AUDITORS_PERFORMANCE"),
        link: "/supervisor/other-auditors-performance",
        name: TPermissions.Supervisor,
        pageName: "SUPERVISOR_OTHER_AUDITORS_PERFORMANCE",
        pageSubName: "SUPERVISOR_OTHER_AUDITORS_PERFORMANCE",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("RESET_PASSWORD"),
        link: "/supervisor/resetPasswords",
        name: TPermissions.Supervisor,
        pageName: "SUPERVISOR_RESET_PASSWORDS",
        pageSubName: "SUPERVISOR_RESET_PASSWORDS",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("FLAGGED_DESCRIPTIONS"),
        link: "/supervisor/flaggedDescription",
        name: TPermissions.Supervisor,
        pageName: "SUPERVISOR_FLAGGED_DESCRIPTIONS",
        pageSubName: "SUPERVISOR_FLAGGED_DESCRIPTIONS",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("SUPERVISOR_RECEIPT_CONFIGURATION"),
        link: "/supervisor/supervisorReceiptConfiguration",
        name: TPermissions.Supervisor,
        pageName: "SUPERVISOR_RECEIPT_CONFIGURATION",
        pageSubName: "SUPERVISOR_RECEIPT_CONFIGURATION",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("SUPERVISOR_RECEIPT_COMPENSATION"),
        link: "/supervisor/supervisorReceiptCompensation",
        name: TPermissions.Supervisor,
        pageName: "SUPERVISOR_RECEIPT_COMPENSATION",
        pageSubName: "SUPERVISOR_RECEIPT_COMPENSATION",
        visibility: !isAnyKhattabaAdmin
      }
    ];
    return supervisorOptionsArray;
  });

  QA_OPTIONS: IComputedValue<TOption[]> = computed(() => {
    const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();

    const qaOptionsArray: TOption[] = [
      {
        title: i18n.get("CHANGE_STATUS"),
        link: "/QA/changeStatus",
        name: TPermissions.QA,
        pageName: "QA_CHANGE_STATUS_DATE",
        pageSubName: "QA_CHANGE_STATUS_DATE",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("CHANGE_EMAIL_SUBSCRIPTION"),
        link: "/QA/changeEmailSubscription",
        name: TPermissions.QA,
        pageName: "QA_CHANGE_EMAIL_SUBSCRIPTION",
        pageSubName: "QA_CHANGE_EMAIL_SUBSCRIPTION",
        visibility: !isAnyKhattabaAdmin
      },

      {
        title: i18n.get("OBTAIN_IOS_RECEIPT_TOKENS"),
        link: "/QA/obtainIosReceiptTokens",
        name: TPermissions.QA,
        pageName: "QA_OBTAIN_IOS_RECEIPT_TOKENS",
        pageSubName: "QA_OBTAIN_IOS_RECEIPT_TOKENS",
        visibility: !isAnyKhattabaAdmin
      }
    ];
    return qaOptionsArray;
  });

  DEV_ZONE_OPTIONS: IComputedValue<TOption[]> = computed(() => {
    const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();

    const developerOptionsArray: TOption[] = [
      {
        title: i18n.get("FLAGGED_CONNECTIONS"),
        link: "/developer/FlaggedConnectionsDashboard",
        name: TPermissions.Developer,
        pageName: "DEVELOPER_FLAGGED_CONNECTIONS",
        pageSubName: "DEVELOPER_FLAGGED_CONNECTIONS",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("ON_CALL_TEAM"),
        link: "/developer/onCallTeam",
        name: TPermissions.Developer,
        pageName: "DEVELOPER_ON_CALL_TEAM",
        pageSubName: "DEVELOPER_ON_CALL_TEAM",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("BLOCKED_REQUESTS"),
        link: "/developer/blockedRequests",
        name: TPermissions.Developer,
        pageName: "DEVELOPER_BLOCKED_REQUESTS",
        pageSubName: "DEVELOPER_BLOCKED_REQUESTS",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("STATS"),
        link: "/developer/statsDashboard",
        name: TPermissions.Developer,
        pageName: "DEVELOPER_STATS",
        pageSubName: "DEVELOPER_STATS",
        visibility: !isAnyKhattabaAdmin
      },

      {
        title: i18n.get("WATCH_ALERTS"),
        link: "/developer/WatchAlerts",
        name: TPermissions.Developer,
        pageName: "DEVELOPER_WATCH_ALERT",
        pageSubName: "DEVELOPER_WATCH_ALERT",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("TURN_OFF_ALARMS"),
        link: "/developer/turnOffAlarms",
        name: TPermissions.Developer,
        pageName: "DEVELOPER_TURN_OFF_ALARMS",
        pageSubName: "DEVELOPER_TURN_OFF_ALARMS",
        visibility: !isAnyKhattabaAdmin
      },

      {
        title: i18n.get("FLUSH"),
        link: "/developer/flushHosts",
        name: TPermissions.Developer,
        pageName: "DEVELOPER_FLUSH_HOSTS",
        pageSubName: "DEVELOPER_FLUSH_HOSTS",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("DEBUG_IMAGE_AI"),
        link: "/developer/DebugImageAI",
        name: TPermissions.Developer,
        pageName: "DEVELOPER_DEBUG_IMAGE_AI",
        pageSubName: "DEVELOPER_DEBUG_IMAGE_AI",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("ADMIN_USAGE_DATA"),
        link: "/developer/adminUsageData",
        name: TPermissions.Developer,
        pageName: "DEVELOPER_ADMIN_USAGE_DATA",
        pageSubName: "DEVELOPER_ADMIN_USAGE_DATA",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("AI_GENERATED_DESCRIPTION_ALL"),
        link: "/developer/aiGeneratedDescriptionAll",
        name: TPermissions.Developer,
        pageName: "DEVELOPER_AI_GENERATED_DESCRIPTION_ALL",
        pageSubName: "DEVELOPER_AI_GENERATED_DESCRIPTION_ALL",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("TRANSFER_USER_PACKAGE"),
        link: "/developer/transferUserPackage",
        name: TPermissions.Developer,
        pageName: "DEVELOPER_TRANSFER_USER_PACKAGE",
        pageSubName: "DEVELOPER_TRANSFER_USER_PACKAGE",
        visibility: !isAnyKhattabaAdmin
      }
    ];
    return developerOptionsArray;
  });

  KHATTABAT_OPTIONS: IComputedValue<TOption[]> = computed(() => {
    const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();

    const khattabatOptionsArray: TOption[] = [
      {
        title: i18n.get("KHATTABAT_DATA"),
        link: "/khattabat/data",
        name: TPermissions.Khattabat,
        pageName: "KHATTABAT_DATA_COLLECTION",
        pageSubName: "KHATTABAT_DATA_COLLECTION",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("TURN_KHATTABAT_ON_OFF"),
        link: "/khattabat/turnOnOff",
        name: TPermissions.Khattabat,
        pageName: "SUPERVISOR_TURN_ON_OFF_KHATTABAT",
        pageSubName: "SUPERVISOR_TURN_ON_OFF_KHATTABAT",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("KHATTABAT_REMUNERATION"),
        link: "/khattabat/remuneration",
        name: TPermissions.Khattabat,
        pageName: "KHATTABAT_REMUNERATION",
        pageSubName: "KHATTABAT_REMUNERATION",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("REASSIGN_KHATTABA"),
        link: "/khattabat/reassignKhattaba",
        name: TPermissions.Khattabat,
        pageName: "DEVELOPER_REASSIGN_KHATTABA",
        pageSubName: "DEVELOPER_REASSIGN_KHATTABA",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("SUPERVISOR_KHATTABAT_FUNNEL_STATS"),
        link: "/khattabat/funnelStats",
        name: TPermissions.Khattabat,
        pageName: "SUPERVISOR_KHATTABAT_FUNNEL_STATS",
        pageSubName: "SUPERVISOR_KHATTABAT_FUNNEL_STATS",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("MATCHMAKING_AUDIT"),
        link: "/khattabat/matchmakingAudit",
        name: TPermissions.Khattabat,
        pageName: "KHATTABAT_MATCHMAKING_AUDIT",
        pageSubName: "KHATTABAT_MATCHMAKING_AUDIT",
        visibility: !isAnyKhattabaAdmin
      }
    ];
    return khattabatOptionsArray;
  });

  COMMUNICATION_OPTIONS: IComputedValue<TOption[]> = computed(() => {
    const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();

    const communicationOptionsArray: TOption[] = [
      {
        title: i18n.get("COMMUNICATIONS_ACCOUNTS_DELETION"),
        link: "/communications/accountsDeletion",
        name: TPermissions.Communications,
        pageName: "COMMUNICATIONS_ACCOUNTS_DELETION",
        pageSubName: "COMMUNICATIONS_ACCOUNTS_DELETION",
        visibility: !isAnyKhattabaAdmin
      },
      {
        title: i18n.get("COMMUNICATIONS_PROMO_CODE_GENERATOR"),
        link: "/communications/promoCodeGenerator",
        name: TPermissions.Communications,
        pageName: "COMMUNICATIONS_PROMO_CODE_GENERATOR",
        pageSubName: "COMMUNICATIONS_PROMO_CODE_GENERATOR",
        visibility: !isAnyKhattabaAdmin
      }
    ];
    return communicationOptionsArray;
  });

  SCSR_OPTIONS: IComputedValue<TOption[]> = computed(() => {
    const scsrOptionsArray: TOption[] = [
      {
        title: i18n.get("GIVE_SUBSCRIPTIONS"),
        link: "/scsr/giveSubscriptions",
        name: TPermissions.SCSR,
        pageName: "SCSR_GIVE_SUBSCRIPTIONS",
        pageSubName: "SCSR_GIVE_SUBSCRIPTIONS",
        visibility: true
      }
    ];
    return scsrOptionsArray;
  });
}

const getRoutingStore = memoize(
  () => new RoutingStore(),
  () => 1
);
export default getRoutingStore;
